/**
 * Pulse Cart Rewards Bar
 *
 * Animation strategy:
 *   - Module-level state survives DOM re-renders (Shopify replaces innerHTML on cart updates).
 *   - Fill bar: snap to last known % → rAF to new % so CSS transition always fires.
 *   - Circles: transition only on state change; snap silently on re-renders.
 *   - Gift unlock: inline celebration text slides in, holds 2.5s, fades to next goal.
 *   - Bar / text success colors: FREE SHIPPING threshold only.
 */

(function () {
  'use strict';

  // ─── Persistent state ───────────────────────────────────────────────────────
  var lastFillPct    = null;   // null = first render ever
  var lastUnlocked   = {};     // threshold (cents) → boolean
  var fillRafPending = false;
  var pendingPct     = null;
  var pubsubTimer    = null;
  var celebrateTimer = null;   // holds celebration timeout handle
  var celebratingEl  = null;   // the textEl that is currently in celebration

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function formatMoney(cents, el) {
    /* Prefer the format string the host element renders into a data attribute
       (set from {{ shop.money_format }} in pulse-cart-rewards.liquid). Fall
       back to window.Shopify.money_format if it exists, then to a generic
       amount-only format as last resort. Avoids the previous hardcoded
       `${{amount}}` USD fallback, which was wrong for any non-USD store. */
    var fmt = (el && el.dataset.moneyFormat)
           || (window.Shopify && window.Shopify.money_format)
           || '{{amount}}';
    var amount = (cents / 100).toFixed(2);
    return fmt
      .replace('{{amount}}',                                  amount)
      .replace('{{amount_no_decimals}}',                      String(Math.floor(cents / 100)))
      .replace('{{amount_with_comma_separator}}',             amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.floor(cents / 100)));
  }

  function snapTo(el, prop, value) {
    el.style.transition = 'none';
    el.style[prop] = value;
    void el.offsetWidth;
    el.style.removeProperty('transition');
  }

  function setColor(el, color) {
    el.style.setProperty('color', color, 'important');
  }

  function applyText(textEl, html, color) {
    textEl.innerHTML = html;
    setColor(textEl, color);
    textEl.querySelectorAll('strong').forEach(function (s) {
      setColor(s, color);
      s.style.fontWeight = '700';
    });
  }

  // ─── Position circles ───────────────────────────────────────────────────────
  var EDGE_PCT = 15;

  function giftPct(i, count) {
    if (count === 1) return 50;
    return EDGE_PCT + (i / (count - 1)) * (100 - 2 * EDGE_PCT);
  }

  function positionGifts(el) {
    var giftEls = el.querySelectorAll('.pulse-cart-rewards__gift');
    var count   = giftEls.length;
    if (count === 0) return;
    giftEls.forEach(function (giftEl, i) {
      giftEl.style.left = giftPct(i, count) + '%';
    });
  }

  // ─── Inline celebration ──────────────────────────────────────────────────────
  // Shows inside the status text area — no floating box.
  // Sequence: slide-in "UNLOCKED / Gift Name" → hold 2.5s → fade → next-goal text.

  function celebrate(textEl, giftName, nextHtml, nextColor, fillColor, textColor) {
    // Always cancel any running celebration so subsequent milestones always show
    clearTimeout(celebrateTimer);
    textEl.classList.remove('pcr-celebrate', 'pcr-celebrate--exit');
    void textEl.offsetWidth; // force reflow so re-adding the class re-triggers animation

    // Single line — same font size as normal status text, no layout shift
    // Format: 🎁 UNLOCKED · Free Tote Bag
    textEl.innerHTML =
      '🎁 <span style="opacity:0.65">UNLOCKED</span> · <strong>' + giftName + '</strong>';
    textEl.classList.add('pcr-celebrate');
    setColor(textEl, textColor);
    var strong = textEl.querySelector('strong');
    if (strong) strong.style.setProperty('color', fillColor, 'important');

    celebratingEl = textEl;

    // After 2.5s: fade out, then show next-goal text
    celebrateTimer = setTimeout(function () {
      textEl.classList.add('pcr-celebrate--exit');
      setTimeout(function () {
        celebrateTimer = null;
        celebratingEl  = null;
        textEl.classList.remove('pcr-celebrate', 'pcr-celebrate--exit');
        applyText(textEl, nextHtml, nextColor);
      }, 280);
    }, 2500);
  }

  // ─── Core update ────────────────────────────────────────────────────────────

  function applyUpdate(el, totalCents) {
    if (!el) return;

    var highest  = parseInt(el.dataset.highestThreshold,  10) || 0;
    var shipping = parseInt(el.dataset.shippingThreshold, 10) || 0;
    if (highest <= 0) return;

    var fillColor        = el.dataset.fillColor        || '#0d0e0e';
    var trackColor       = el.dataset.trackColor       || '#e5e7eb';
    var successFillColor = el.dataset.successFillColor || '#16a34a';
    var textColor        = el.dataset.textColor        || '#6b7280';
    var successTextColor = el.dataset.successTextColor || '#16a34a';

    // ── Calculate fill % ────────────────────────────────────────────────────
    var giftEls   = el.querySelectorAll('.pulse-cart-rewards__gift');
    var giftCount = giftEls.length;
    var pct;

    if (giftCount > 0) {
      var positions = [];
      giftEls.forEach(function (giftEl, i) {
        positions.push({
          cents:     parseInt(giftEl.dataset.thresholdCents, 10) || 0,
          visualPct: giftPct(i, giftCount)
        });
      });
      positions.unshift({ cents: 0, visualPct: 0 });

      // Determine the endpoint so the bar reaches exactly 100% at the highest milestone.
      // Case A: shipping (or another goal) is higher than the last gift — add it as the
      //         100% endpoint so the bar keeps filling past the last circle.
      // Case B: last gift IS the highest milestone — remap that circle's visualPct to 100
      //         so the bar fills completely the moment it's crossed.
      var lastPos = positions[positions.length - 1];
      if (highest > lastPos.cents) {
        positions.push({ cents: highest, visualPct: 100 });
      } else {
        lastPos.visualPct = 100;
      }

      pct = 0;
      for (var s = 1; s < positions.length; s++) {
        if (totalCents <= positions[s].cents) {
          var segStart = positions[s - 1];
          var segEnd   = positions[s];
          var ratio    = (totalCents - segStart.cents) / (segEnd.cents - segStart.cents);
          pct = segStart.visualPct + ratio * (segEnd.visualPct - segStart.visualPct);
          break;
        }
        pct = 100;
      }
      pct = Math.min(pct, 100);
    } else {
      pct = Math.min((totalCents / highest) * 100, 100);
    }

    // ── Progress bar fill animation ──────────────────────────────────────────
    var fillEl  = el.querySelector('.pulse-cart-rewards__fill');
    var trackEl = el.querySelector('.pulse-cart-rewards__track');

    if (fillEl) {
      if (fillRafPending) {
        pendingPct = pct;
      } else if (lastFillPct !== null) {
        snapTo(fillEl, 'width', lastFillPct + '%');
        pendingPct     = pct;
        fillRafPending = true;
        requestAnimationFrame(function () {
          fillRafPending = false;
          fillEl.style.width = pendingPct + '%';
          lastFillPct = pendingPct;
        });
      } else {
        fillEl.style.width = pct + '%';
        lastFillPct = pct;
      }
    }
    if (trackEl) trackEl.setAttribute('aria-valuenow', Math.round(pct));

    // ── Bar color ────────────────────────────────────────────────────────────
    // Success colors ONLY apply in free-shipping-only mode (no gift milestones).
    // If any gifts are configured, always use fillColor regardless of shipping.
    var shippingUnlocked = shipping > 0 && totalCents >= shipping;
    var useSuccessColors = shippingUnlocked && giftCount === 0;
    if (fillEl) {
      fillEl.style.backgroundColor = useSuccessColors ? successFillColor : fillColor;
    }

    // ── Gift circles ─────────────────────────────────────────────────────────
    var LOCKED_ICON_COLOR = '#9ca3af';
    var newlyUnlockedGift = null;

    giftEls.forEach(function (giftEl) {
      var threshold = parseInt(giftEl.dataset.thresholdCents, 10) || 0;
      var unlocked  = totalCents >= threshold;
      var prevState = lastUnlocked[threshold];
      var didChange = (prevState !== undefined && prevState !== unlocked);

      if (didChange && unlocked) {
        var nameEl = giftEl.querySelector('.pulse-cart-rewards__gift-name');
        var rawName = nameEl ? nameEl.textContent.trim() : '';
        // Always trigger the celebration on unlock — even if the gift has no
        // label set in theme settings (use a generic "Reward" fallback so the
        // celebration still fires for nameless gifts).
        newlyUnlockedGift = rawName || 'Reward';
      }

      giftEl.classList.toggle('is-unlocked', unlocked);

      var circle = giftEl.querySelector('.pulse-cart-rewards__circle');
      if (!circle) { lastUnlocked[threshold] = unlocked; return; }

      var bgColor   = unlocked ? fillColor : trackColor;
      var iconColor = unlocked ? trackColor : LOCKED_ICON_COLOR;
      var svg       = circle.querySelector('svg');

      if (didChange) {
        circle.style.backgroundColor = bgColor;
        if (svg) svg.style.stroke = iconColor;
        else     circle.style.color = iconColor;
        // Bounce the circle on unlock
        if (unlocked) {
          circle.classList.remove('pcr-bounce');
          void circle.offsetWidth;
          circle.classList.add('pcr-bounce');
          setTimeout(function () { circle.classList.remove('pcr-bounce'); }, 600);
        }
      } else {
        snapTo(circle, 'backgroundColor', bgColor);
        if (svg) snapTo(svg, 'stroke', iconColor);
        else     snapTo(circle, 'color', iconColor);
      }

      lastUnlocked[threshold] = unlocked;
    });

    // ── Status text ──────────────────────────────────────────────────────────
    var textEl      = el.querySelector('.pulse-cart-rewards__text');
    var progressTxt = el.dataset.progressText || 'Spend {amount} more for free shipping';
    var successTxt  = el.dataset.successText  || "You've unlocked free shipping!";
    if (!textEl) return;

    // Build milestone list
    var milestones = [];
    giftEls.forEach(function (giftEl) {
      var cents = parseInt(giftEl.dataset.thresholdCents, 10) || 0;
      if (cents > 0) {
        var nameEl = giftEl.querySelector('.pulse-cart-rewards__gift-name');
        milestones.push({ cents: cents, isGift: true, name: nameEl ? nameEl.textContent.trim() : '' });
      }
    });
    if (shipping > 0) milestones.push({ cents: shipping, isGift: false });
    milestones.sort(function (a, b) { return a.cents - b.cents; });

    var next = null;
    for (var i = 0; i < milestones.length; i++) {
      if (totalCents < milestones[i].cents) { next = milestones[i]; break; }
    }

    // Build the "next goal" HTML (used after celebration or directly).
    // Gift milestones get their own message (even when the name is blank —
    // falls back to "your reward") so the user-facing text matches the
    // active milestone type instead of always showing the free-shipping
    // copy when the next goal is actually a gift.
    var nextHtml;
    var nextColor;
    if (!next) {
      // Everything unlocked
      var lastMilestone = milestones.length > 0 ? milestones[milestones.length - 1] : null;
      if (lastMilestone && lastMilestone.isGift) {
        var lastName = lastMilestone.name || 'your reward';
        nextHtml  = 'You\'ve unlocked <strong>' + lastName + '</strong>!';
      } else {
        nextHtml  = successTxt;
      }
      nextColor = useSuccessColors ? successTextColor : textColor;
    } else {
      var remaining = next.cents - totalCents;
      var formatted = formatMoney(remaining, el);
      if (next.isGift) {
        var nextName = next.name || 'your next reward';
        nextHtml = 'Spend <strong>' + formatted + '</strong> more to unlock <strong>' + nextName + '</strong>';
      } else {
        nextHtml = progressTxt.replace('{amount}', '<strong>' + formatted + '</strong>');
      }
      nextColor = useSuccessColors ? successTextColor : textColor;
    }

    if (!next) el.classList.add('pulse-cart-rewards--success');
    else       el.classList.remove('pulse-cart-rewards--success');

    // Celebrate on ANY gift unlock — including the last one.
    // nextHtml/nextColor are already computed above for both cases.
    if (newlyUnlockedGift) {
      celebrate(textEl, newlyUnlockedGift, nextHtml, nextColor, fillColor, textColor);
    } else if (celebratingEl === textEl) {
      // Celebration running, nothing new unlocked — MutationObserver re-fire, ignore.
    } else {
      applyText(textEl, nextHtml, nextColor);
    }
  }

  // ─── Run from re-rendered DOM ────────────────────────────────────────────────

  function runFromDOM() {
    var el = document.querySelector('.pulse-cart-rewards');
    if (!el) return;
    positionGifts(el);
    var totalCents = parseInt(el.dataset.cartTotal, 10) || 0;
    applyUpdate(el, totalCents);
  }

  // ─── Subscribe to pubsub ────────────────────────────────────────────────────

  function setupPubsub() {
    if (typeof subscribe !== 'function') return;
    subscribe('cart-update', function (event) {
      var total = event && event.cartData && event.cartData.total_price;
      if (typeof total !== 'number') return;
      clearTimeout(pubsubTimer);
      pubsubTimer = setTimeout(function () {
        var el = document.querySelector('.pulse-cart-rewards');
        if (el) {
          positionGifts(el);
          applyUpdate(el, total);
        } else {
          setTimeout(function () {
            var el2 = document.querySelector('.pulse-cart-rewards');
            if (!el2) return;
            positionGifts(el2);
            applyUpdate(el2, total);
          }, 300);
        }
      }, 100);
    });
  }

  // ─── MutationObserver ───────────────────────────────────────────────────────

  function setupObserver() {
    function attachObserver() {
      var cartDrawer = document.getElementById('CartDrawer');
      if (!cartDrawer) return false;
      var pending = false;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          runFromDOM();
        });
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
      return true;
    }
    if (!attachObserver()) {
      var bodyObserver = new MutationObserver(function () {
        if (attachObserver()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true, subtree: false });
    }
  }

  // ─── Boot ────────────────────────────────────────────────────────────────────

  function boot() {
    runFromDOM();
    setupPubsub();
    setupObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
